import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { fetchApi } from '../../utils/api';
import LoadingModal from '../bonus/api-loading-modal';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const emptyAnswers = () => ({
  answer_relationship: '',
  answer_bet: '',
  answer_superpower: '',
  answer_anything_else: '',
});

// The questionnaire, personalized with the founder's first name
const questions = (founder) => ([
  {
    key: 'answer_relationship',
    title: `How well do you know ${founder}?`,
    hint: 'Please describe your relationship and background.',
    required: true,
  },
  {
    key: 'answer_bet',
    title: `Why should we bet on ${founder}?`,
    hint: null,
    required: true,
  },
  {
    key: 'answer_superpower',
    title: `What is ${founder}'s superpower?`,
    hint: 'E.g. strongest trait or skill.',
    required: true,
  },
  {
    key: 'answer_anything_else',
    title: 'Anything else we should know? (Optional)',
    hint: null,
    required: false,
  },
]);

function Reference() {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState(emptyAnswers());

  useEffect(() => {
    const fetchReference = async () => {
      try {
        setLoading(true);
        const response = await fetchApi('api/public/reference', {
          method: 'GET',
          params: { id, token }
        });
        setReference(response.data);
        if (response.data.already_submitted) {
          setSubmitted(true);
          setAnswers({ ...emptyAnswers(), ...(response.data.answers || {}) });
        }
      } catch (err) {
        navigate('/error', { replace: true });
        return;
      } finally {
        setLoading(false);
      }
    };

    const validId = id && /^\d+$/.test(id) && parseInt(id, 10) > 0;
    const validToken = token && UUID_REGEX.test(token);

    if (validId && validToken) {
      fetchReference();
    } else {
      navigate('/error', { replace: true });
    }
  }, [id, token, navigate]);

  const founder = reference?.founder_first_name || 'the founder';
  const founderFullName = reference?.founder_full_name || founder;
  const questionList = questions(founder);

  const handleAnswerChange = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  // Answers start as a single line and grow with their content
  const autoResize = (el) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    for (const q of questionList) {
      if (q.required && !answers[q.key].trim()) {
        setError(`Please answer: ${q.title}`);
        return;
      }
    }

    try {
      setSubmitting(true);
      await fetchApi('api/public/reference', {
        method: 'POST',
        body: JSON.stringify({
          id: parseInt(id, 10),
          token,
          answer_relationship: answers.answer_relationship.trim(),
          answer_bet: answers.answer_bet.trim(),
          answer_superpower: answers.answer_superpower.trim(),
          answer_anything_else: answers.answer_anything_else.trim() || null,
        }),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>LeapYear | Reference</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <LoadingModal isOpen={submitting} message="Submitting your reference..." />

      <main className="container" role="main">
        {loading && (
          <div className="finalist-loader">
            <div className="finalist-loader-dot" />
          </div>
        )}

        {!loading && reference && submitted && (
          <section className="content-section fade-in" aria-labelledby="reference-title">
            <div className="finalist-welcome">
              <h1 className="page-title" id="reference-title">Thank You!</h1>
              <p className="info-text">
                We've received your reference for <strong>{founderFullName}</strong>.
              </p>
              <p className="info-text">
                We really appreciate you taking the time.
              </p>
              <hr className="light-divider" />
              <h2 className="form-section-title">Your Reference</h2>
              {questionList.map((q) => (
                answers[q.key] ? (
                  <div className="form-group" key={q.key}>
                    <p className="info-text" style={{ marginBottom: '0.25rem', color: 'rgba(255,255,255,0.6)' }}>{q.title}</p>
                    <p className="info-text">{answers[q.key]}</p>
                  </div>
                ) : null
              ))}
              <hr className="light-divider" />
              <p className="info-text">
                <a href="/" style={{ color: 'white' }}>Learn more about LeapYear?</a>
              </p>
            </div>
          </section>
        )}

        {!loading && reference && !submitted && (
          <form onSubmit={handleSubmit} className="fade-in">
            {/* Welcome Section */}
            <section className="content-section" aria-labelledby="reference-title">
              <div className="finalist-welcome">
                <p className="section-label finalist-page-title">LeapYear Reference</p>
                <h1 className="page-title" id="reference-title">
                  Reference for {founderFullName}
                </h1>
                <p className="info-text">
                  Hi {reference.reference_name} —
                </p>
                <p className="info-text">
                  {founderFullName} listed you as a reference for their LeapYear application. LeapYear
                  is a first-believer fund for young founders. We'd love to hear your perspective
                  on {founder}. A few short questions below. Simple direct answers work best. Thank you!
                </p>
              </div>
            </section>

            <hr className="light-divider" />

            {questionList.map((q, index) => (
              <React.Fragment key={q.key}>
                {index > 0 && <hr className="light-divider" />}
                <section className="content-section">
                  <h2 className={`form-section-title${q.required ? ' required' : ''}`}>{q.title}</h2>
                  {q.hint && <p className="info-text">{q.hint}</p>}
                  <div className="form-group">
                    <textarea
                      className={`form-input${error && q.required && !answers[q.key].trim() ? ' error' : ''}`}
                      placeholder="your answer..."
                      value={answers[q.key]}
                      onChange={(e) => {
                        handleAnswerChange(q.key, e.target.value);
                        autoResize(e.target);
                      }}
                      rows={1}
                      maxLength={5000}
                      aria-label={q.title}
                      style={{ resize: 'none', overflow: 'hidden', minHeight: 0 }}
                    />
                  </div>
                </section>
              </React.Fragment>
            ))}

            {/* Error message */}
            {error && (
              <p className="info-text" style={{ color: 'rgba(255, 0, 0, 0.8)' }} role="alert">
                {error}
              </p>
            )}

            {/* Submit */}
            <button type="submit" className="submit-button" disabled={submitting}>
              Submit Reference
            </button>
          </form>
        )}
        <div style={{ height: '8rem' }} />
      </main>
    </>
  );
}

export default Reference;
